import React, { ReactNode, useMemo } from "react";

import { IgLink, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListDto,
  ExperienceTestTypeDto,
  GenericExperienceFe,
  IntegrationCompleteStatuses,
  WebUiScopesConfigurationKeyType,
} from "@intelligems/ig-types";
import Stack from "@mui/material/Stack";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useBooleanWebUiScopeMap } from "@src/components/LimitAccess/check-scopes-hooks";
import { useCheckSubscriptionStatus } from "@src/components/LimitAccess/check-subscriptions-hooks";
import { audiencesAreEquivalent } from "@src/utils/audienceEquality";
import { contactSupport } from "@src/utils/contactSupport";
import {
  priceTestConflictFaqLink,
  shippingTestConflictFaqLink,
} from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

export const PostPurchaseAudienceOverlapMessage = (
  <Stack spacing={1}>
    <div>
      <strong>Audience overlap detected.</strong> Only one post-purchase upsell
      can be shown per order.
    </div>
    <div>
      This upsell targets the same audience as an upsell that's already live, so
      it can't be started right now.
    </div>
    <div>
      In the meantime, add this offer as an additional rule on your live upsell.
    </div>
    <div>
      We're building multi-upsell support — this limit will be removed soon.
    </div>
  </Stack>
);

const CART_TRANSFORM_CONFLICT_FAQ_LINK: Record<
  "Price Test" | "Shipping Test",
  string
> = {
  "Price Test": priceTestConflictFaqLink,
  "Shipping Test": shippingTestConflictFaqLink,
};

export const CartTransformConflictMessage = (
  conflictingTestLabel: "Price Test" | "Shipping Test"
) => {
  const blockedTestLabel =
    conflictingTestLabel === "Price Test" ? "Shipping Test" : "Price Test";

  return (
    <IgTypography>
      You can't have a shipping test and a price test active at the same time.
      End the active {conflictingTestLabel} to start this one, or{" "}
      <IgLink onClick={contactSupport}>contact support</IgLink> for help. See{" "}
      <IgLink
        onClick={() =>
          openNewTab(CART_TRANSFORM_CONFLICT_FAQ_LINK[blockedTestLabel], {
            mixpanelTracker: "Price and Shipping Test Conflict FAQ",
          })
        }
      >
        our docs
      </IgLink>{" "}
      for more details.
    </IgTypography>
  );
};

export const isCartTransformTestConflictActive = (
  experiences: Pick<ExperienceListDto, "id" | "status" | "testTypes">[],
  cartTransformMode: string | undefined,
  currentExperienceId: string | undefined,
  testType: "hasTestPricing" | "hasTestShipping"
): boolean => {
  if (cartTransformMode !== "expand") return false;
  const conflictingTestType =
    testType === "hasTestPricing" ? "hasTestShipping" : "hasTestPricing";
  return experiences.some(
    (experience) =>
      experience.id !== currentExperienceId &&
      experience.status === "started" &&
      experience.testTypes[conflictingTestType]
  );
};

const CreateTestTypeMap: Record<
  keyof ExperienceTestTypeDto,
  Lowercase<WebUiScopesConfigurationKeyType>
> = {
  hasTestPricing: "create_price_experiment",
  hasTestShipping: "create_shipping_experiment",
  hasTestContentTemplate: "create_template_experiment",
  hasTestContentTheme: "create_theme_experiment",
  hasTestContent: "create_onsite_edits_experiment",
  hasTestCampaign: "create_discount_experiment",
  hasTestContentAdvanced: "create_content_experiment", // deprecated?
  hasTestContentOnsite: "create_onsite_edits_experiment",
  hasTestContentUrl: "create_redirect_experiment",
  hasTestOnsiteInjections: "create_onsite_edits_experiment",
  hasTestCheckoutBlocks: "create_checkout_block_test",
  hasTestPostPurchase: "create_checkout_block_test",
  hasTestOnsiteUpsell: "create_onsite_upsell",
};

const StartTestTypeMap: Record<
  keyof ExperienceTestTypeDto,
  Lowercase<WebUiScopesConfigurationKeyType>
> = {
  hasTestPricing: "start_price_experiment",
  hasTestShipping: "start_shipping_experiment",
  hasTestContentTemplate: "start_template_experiment",
  hasTestContentTheme: "start_theme_experiment",
  hasTestContent: "start_onsite_edits_experiment",
  hasTestCampaign: "start_discount_experiment",
  hasTestContentAdvanced: "start_content_experiment", // deprecated?
  hasTestContentOnsite: "start_onsite_edits_experiment",
  hasTestContentUrl: "start_redirect_experiment",
  hasTestOnsiteInjections: "start_onsite_edits_experiment",
  hasTestCheckoutBlocks: "start_checkout_block_test",
  hasTestPostPurchase: "start_checkout_block_test",
  hasTestOnsiteUpsell: "start_onsite_upsell",
};

const FriendlyTestTypeMap: Record<keyof ExperienceTestTypeDto, string> = {
  hasTestPricing: "pricing",
  hasTestShipping: "shipping",
  hasTestContentTemplate: "template",
  hasTestContentTheme: "theme",
  hasTestContent: "onsite edits",
  hasTestCampaign: "discount",
  hasTestContentAdvanced: "content", // deprecated?
  hasTestContentOnsite: "onsite edits",
  hasTestContentUrl: "redirect",
  hasTestOnsiteInjections: "onsite edits",
  hasTestCheckoutBlocks: "checkout",
  hasTestPostPurchase: "post-purchase",
  hasTestOnsiteUpsell: "onsite upsell",
};

export const useIsShippingTestAlreadyActive = () => {
  const experiencesList = useGetExperiencesList({ category: "experiment" });

  return useMemo(
    () =>
      (experiencesList.data?.experiencesList
        ?.filter((experience) => experience.status === "started")
        ?.filter((experience) => experience.testTypes.hasTestShipping)
        ?.length || 0) > 0,
    [experiencesList]
  );
};

export const useIsCartTransformTestConflictActive = (
  experienceId: string | undefined,
  testType: "hasTestPricing" | "hasTestShipping",
  enabled = true
) => {
  const conflictingModule =
    testType === "hasTestPricing" ? "shipping" : "pricing";
  const experiencesList = useGetExperiencesList({
    module: conflictingModule,
    enabled,
  });
  const cartTransformMode = useConfiguration<string>(
    "cart_transform_mode"
  )?.value;

  return useMemo(
    () =>
      isCartTransformTestConflictActive(
        experiencesList.data?.experiencesList ?? [],
        cartTransformMode,
        experienceId,
        testType
      ),
    [experiencesList, cartTransformMode, experienceId, testType]
  );
};

export const useHasLivePostPurchaseWithSameAudience = (
  experience: GenericExperienceFe
) => {
  const experiments = useGetExperiencesList({ category: "experiment" });
  const personalizations = useGetExperiencesList({
    category: "personalization",
  });

  return useMemo(() => {
    if (!experience.testTypes?.hasTestPostPurchase) return false;
    const all = [
      ...(experiments.data?.experiencesList ?? []),
      ...(personalizations.data?.experiencesList ?? []),
    ];
    return all.some(
      (other) =>
        other.id !== experience.id &&
        other.status === "started" &&
        other.testTypes.hasTestPostPurchase &&
        other.audience?.enabled !== false &&
        audiencesAreEquivalent(other.audience, experience.audience)
    );
  }, [experience, experiments.data, personalizations.data]);
};

export const useIsTestLimitHit = () => {
  const experiencesList = useGetExperiencesList({ category: "experiment" });
  const org = useGetOrg();

  return useMemo(() => {
    const maxTests: number = org.data.org.configurations.find(
      (c) => c.key === "concurrent_tests"
    )?.value;
    const activeTestsCount =
      experiencesList.data?.experiencesList.filter(
        (ex) => ex.status === "started"
      ).length || 0;
    return activeTestsCount >= maxTests;
  }, [experiencesList]);
};

export const useIsPriceTestIntegrationReady = () => {
  const organization = useGetOrg();
  const isHeadless = useConfiguration("is_headless_storefront");
  const priceSelectors = useConfiguration<string[]>(
    "additional_price_query_selectors"
  );

  return useMemo(
    () =>
      Boolean(
        IntegrationCompleteStatuses.includes(
          organization.data.org.integrationStatus
        ) ||
        (priceSelectors?.value || []).length > 0 ||
        isHeadless?.value
      ),
    [organization.data.org.integrationStatus, priceSelectors, isHeadless?.value]
  );
};

export const useCanStartExperience = (
  experience: GenericExperienceFe
): { canStart: boolean; disabledMessage: ReactNode } => {
  const isSubscriptionActive = useCheckSubscriptionStatus();
  const scopeMap = useBooleanWebUiScopeMap();
  const isPriceTestIntegrationReady = useIsPriceTestIntegrationReady();
  const isShippingTestAlreadyActive = useIsShippingTestAlreadyActive();
  const isPricingCartTransformConflict = useIsCartTransformTestConflictActive(
    experience.id,
    "hasTestPricing",
    Boolean(experience.testTypes.hasTestPricing)
  );
  const isShippingCartTransformConflict = useIsCartTransformTestConflictActive(
    experience.id,
    "hasTestShipping",
    Boolean(experience.testTypes.hasTestShipping)
  );
  const isTestLimitHit = useIsTestLimitHit();
  const hasLivePostPurchaseWithSameAudience =
    useHasLivePostPurchaseWithSameAudience(experience);
  const isPostPurchaseCheckoutEnabled = useConfiguration(
    "enable_post_purchase_checkout"
  )?.value;
  const isAdmin = useIsAdmin();

  return useMemo(() => {
    const category =
      experience.category === "experiment" ? "test" : "personalization";

    if (!experience) {
      return { canStart: false, disabledMessage: "" };
    }

    if (!isSubscriptionActive) {
      return {
        canStart: false,
        disabledMessage: `Approve billing to start this ${category}`,
      };
    }

    const missingTestTypes = Object.entries(experience.testTypes)
      .map(([key, value]) => {
        if (value) {
          let scopeKey = StartTestTypeMap[key as keyof ExperienceTestTypeDto];

          if (
            key === "hasTestCampaign" &&
            experience.category === "personalization"
          ) {
            scopeKey = "start_offer";
          }

          const result = scopeMap[scopeKey];
          if (!result) {
            return FriendlyTestTypeMap[key as keyof ExperienceTestTypeDto];
          }
        }
        return null;
      })
      .filter((s): s is string => Boolean(s));

    const hasNecessaryScopes = missingTestTypes.length === 0;

    // Admins bypass plan-derived limits (concurrent-test limit and plan
    // scopes) but still hit the operational checks below.
    if (isTestLimitHit && !isAdmin) {
      return {
        canStart: false,
        disabledMessage: `You have reached the maximum number of active tests. End an existing test to start a new one.`,
      };
    }

    if (!hasNecessaryScopes && !isAdmin) {
      return {
        canStart: false,
        disabledMessage: `Upgrade your plan to start this ${missingTestTypes.join(", ")} ${category}`,
      };
    }

    if (experience.testTypes.hasTestPricing) {
      if (!isPriceTestIntegrationReady) {
        return {
          canStart: false,
          disabledMessage: `Complete your pricing integration to start this ${category}`,
        };
      }
      if (isPricingCartTransformConflict && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: CartTransformConflictMessage("Shipping Test"),
        };
      }
    }

    if (experience.testTypes.hasTestShipping) {
      if (isShippingTestAlreadyActive && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: `Only one shipping test can be run at a time. End your current shipping test to start a new one.`,
        };
      }
      if (isShippingCartTransformConflict && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: CartTransformConflictMessage("Price Test"),
        };
      }
    }

    if (experience.testTypes.hasTestPostPurchase) {
      if (!isPostPurchaseCheckoutEnabled && experience.status !== "started") {
        return {
          canStart: false,
          disabledMessage: `Launching post purchase coming soon`,
        };
      }
      if (
        hasLivePostPurchaseWithSameAudience &&
        experience.status !== "started"
      ) {
        return {
          canStart: false,
          disabledMessage: PostPurchaseAudienceOverlapMessage,
        };
      }
    }

    return {
      canStart: true,
      disabledMessage: "",
    };
  }, [
    experience,
    isSubscriptionActive,
    scopeMap,
    isPriceTestIntegrationReady,
    isShippingTestAlreadyActive,
    isPricingCartTransformConflict,
    isShippingCartTransformConflict,
    isTestLimitHit,
    hasLivePostPurchaseWithSameAudience,
    isPostPurchaseCheckoutEnabled,
    isAdmin,
  ]);
};

export const useCanCreateExperience = (
  experience: GenericExperienceFe
): { canCreate: boolean; disabledMessage: string } => {
  const isSubscriptionActive = useCheckSubscriptionStatus();
  const scopeMap = useBooleanWebUiScopeMap();
  const isPriceTestIntegrationReady = useIsPriceTestIntegrationReady();
  const isAdmin = useIsAdmin();
  return useMemo(() => {
    const category =
      experience.category === "experiment" ? "test" : "personalization";

    if (!experience) {
      return { canCreate: false, disabledMessage: "" };
    }

    if (!isSubscriptionActive) {
      return {
        canCreate: false,
        disabledMessage: `Approve billing to create this ${category}`,
      };
    }

    const missingTestTypes: string[] = [];
    const hasNecessaryScopes = Object.entries(experience.testTypes).every(
      ([key, value]) => {
        if (value) {
          let scopeKey = CreateTestTypeMap[key as keyof ExperienceTestTypeDto];

          if (
            key === "hasTestCampaign" &&
            experience.category === "personalization"
          ) {
            scopeKey = "create_offer";
          }

          const result = scopeMap[scopeKey];
          if (!result) {
            missingTestTypes.push(
              FriendlyTestTypeMap[key as keyof ExperienceTestTypeDto]
            );
          }
          return result;
        }
        return true;
      }
    );

    if (!hasNecessaryScopes && !isAdmin) {
      return {
        canCreate: false,
        disabledMessage: `Upgrade your plan to create this ${missingTestTypes.join(", ")} ${category}`,
      };
    }

    return {
      canCreate: true,
      disabledMessage: "",
    };
  }, [
    experience,
    isSubscriptionActive,
    scopeMap,
    isPriceTestIntegrationReady,
    isAdmin,
  ]);
};
